/* Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V4
 * Hallmark · component: site voucher notice · genre: playful · theme: project tokens (orange --primary)
 * layout: single line — ticket mark, offer sentence, copy affordance, all one control
 * states: default · hover · focus · active · disabled · loading · error · success
 * One voucher, one line. The whole strip is the copy control, so there is no nested
 * interactive and the touch target is the full width of the results column.
 */
import { Link } from '@inertiajs/react';
import VoucherDetailInertiaController from '@/actions/App/Http/Controllers/VoucherDetailInertiaController';
import { useVoucherCopy } from '@/hooks/use-voucher-copy';
import { cn } from '@/lib/utils';
import { type SuggestedVouchers } from '@/types/search';
import { AlertTriangle, CheckCheck, Copy, Loader2, Ticket } from 'lucide-react';

interface SiteVoucherNoticeProps {
  site?: SuggestedVouchers['site'];
  siteName?: string;
  className?: string;
}

export function SiteVoucherNotice({ site, siteName, className }: SiteVoucherNoticeProps) {
  const { copyState, copy } = useVoucherCopy();

  if (!site?.voucher) {
    return null;
  }

  const { voucher } = site;
  const isDisabled = copyState === 'copying' || !voucher.code;

  // Two whole sentences rather than shared fragments: the global form drops the
  // prefix, which turns `nhập` into the sentence opener and capitalises it. Patching
  // that at render time is how the copy quietly goes wrong.
  const showPrefix = site.scope === 'site' && Boolean(siteName);

  const StateIcon = {
    idle: Copy,
    copying: Loader2,
    copied: CheckCheck,
    error: AlertTriangle,
  }[copyState];

  const stateLabel = {
    idle: 'Copy',
    copying: 'Đang copy',
    copied: 'Đã copy',
    error: 'Thử lại',
  }[copyState];

  return (
    <div className={cn('mb-4', className)}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => copy(voucher.code)}
        aria-label={`Sao chép mã ưu đãi ${voucher.code}`}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-xl border-2 border-dashed px-3 py-2.5 text-left',
          'border-primary/40 bg-primary/5',
          'motion-safe:transition-[background-color,border-color,transform] motion-safe:duration-150',
          // The focus ring shows instantly — it is never part of the transition above.
          'focus-visible:ring-primary/60 focus-visible:ring-[3px] focus-visible:outline-none',
          !isDisabled &&
            'hover:border-primary/50 hover:bg-primary/10 cursor-pointer motion-safe:hover:-translate-y-px motion-safe:active:translate-y-0',
          isDisabled && 'cursor-not-allowed opacity-60',
          copyState === 'copied' && 'border-green-600/40 bg-green-600/5',
          copyState === 'error' && 'border-amber-500/70 bg-amber-500/10',
        )}
      >
        <Ticket className="text-primary size-4 shrink-0" />

        {/* Body copy, free to wrap. The clickable label is the state pill below,
            which stays on one line at every width. */}
        <span className="min-w-0 flex-1 text-sm text-gray-700">
          {showPrefix && <span className="font-semibold text-gray-900">Ưu đãi từ {siteName}: </span>}
          {showPrefix ? 'nhập mã ' : 'Nhập mã '}
          <code className="font-mono font-bold tracking-wider text-gray-900">{voucher.code}</code>
          {voucher.discount_label && (
            <>
              {' giảm ngay '}
              <span className="text-primary-ink font-semibold">{voucher.discount_label}</span>
            </>
          )}
        </span>

        <span
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium',
            'text-primary-ink bg-white/70',
            copyState === 'copied' && 'bg-green-600 text-white',
            copyState === 'error' && 'bg-amber-500 text-white',
          )}
        >
          <StateIcon className={cn('size-3', copyState === 'copying' && 'motion-safe:animate-spin')} />
          {stateLabel}
        </span>
      </button>

      {/* The strip itself copies; the terms live a click away. */}
      <Link
        href={VoucherDetailInertiaController.show(voucher.code).url}
        className="text-primary-ink focus-visible:ring-primary/60 mt-2 inline-block rounded-sm text-xs font-medium whitespace-nowrap underline underline-offset-2 hover:decoration-2 focus-visible:ring-[3px] focus-visible:outline-none"
      >
        Xem chi tiết ưu đãi
      </Link>

      <p aria-live="polite" className="sr-only">
        {copyState === 'copied' ? `Đã sao chép mã ${voucher.code}` : ''}
        {copyState === 'error' ? 'Không sao chép được. Hãy chạm giữ vào mã để chọn và sao chép thủ công.' : ''}
      </p>
    </div>
  );
}
